/*
 * sh-search-relevance.js
 * SH Updated: August 2026 - relevance-first search, tight-tier query builder.
 *
 * WHY THIS EXISTS
 * Plain storefront search ORs the terms, so "route 66" returns everything
 * matching "route" OR "66" (~200 results, and the number drifts between
 * identical requests). Shopify's field-scoped syntax ANDs across title+tags
 * and is deterministic, so we rewrite the query on submit:
 *
 *   route 66  ->  (title:route OR tag:route) AND (title:66 OR tag:66)
 *
 * Verified live on foundimage.com, 3 consecutive runs each:
 *   route 66              plain 199/202/199   tight  50/50/50
 *   bloomington indiana   plain  47/47/47     tight   8/8/8
 *   greetings from bloomington  plain 1000    tight   3/3/3
 *
 * Each term must appear in the title OR the tags, and ALL terms must match.
 * That is deliberately field-agnostic: "Bloomington" lives in the title while
 * "Indiana" lives only in the tags, so a quoted phrase returns 0 on Cate's own
 * flagship example. This shape does not care which field a word lives in.
 *
 * TWO PARAMS
 *   q   the machine query Shopify actually searches (field-scoped)
 *   qt  the human terms, for display and for the "broaden" link
 *
 * NO-JS FALLBACK: if this file does not run, the form submits q as typed and
 * the visitor gets today's plain search. Nothing breaks, it just is not tiered.
 */

(function () {
  'use strict';

  /*
   * Stopwords are stripped because the tier ANDs every term: one word that is
   * in no title and no tag collapses the whole result set to zero.
   * Verified: "postcards of route 66" -> 0/0/0 with "of", 2/2/2 without it.
   *
   * SH Updated: August 2026 - 'will' REMOVED from this list. It was stripping a
   * real subject word: "Will Rogers" collapsed to (title:rogers OR tag:rogers),
   * which pulled Rogers & Smith silverware (tag `Rogers`) into the tight tier.
   * On a complete enumeration of the 58,771 published retail products, whole-word
   * `will` appears in 84 titles: 24 are Will Rogers, ~44 are the "Will You Be My
   * Valentine?" card caption, and only about a dozen are modal filler. Its
   * (title:will OR tag:will) clause reaches 119 products, concentrated on exactly
   * those two subjects. Measured effect: "Will Rogers" 41 -> 29 with all 12 false
   * positives gone (both napkin rings, both Rogers & Smith tea sets, Roy Rogers,
   * Rogers House, Rogers Door); "Will You Be My Valentine" 76 -> 54; and NO other
   * query in a 30-query battery changes at all.
   *
   * NO OTHER WORD QUALIFIES, and there is no frequency rule that separates them:
   * `will` is the 6th RAREST of the 31 by clause reach, so any threshold picks the
   * wrong words. Ten of the remaining words are also live two-letter product-code
   * prefixes (IT- Italy 389, OR- Oregon 493, AT- 514, AN- 431, IN- Indiana 305,
   * AS- 225, IS- 162, IF- 156, BY- 100, BE- 65), so preserving them would drag
   * whole geographic categories into an AND clause. 'it' looks tempting and is not:
   * there are ZERO "It Girl" titles in the catalogue.
   *
   * BAR FOR ANY FUTURE REMOVAL, both conditions, against a fresh complete bulk
   * enumeration: (i) attested as a proper name or printed caption in >= 50
   * published titles, and (ii) its clause reaches >= 100 published products.
   * `will` = 68 attested / 119 reach. `there` (20/25) and `was` (8/10) fail.
   *
   * NOT YET MEASURED ON WHOLESALE. The two catalogues have already diverged once
   * on keyword vocabulary; re-run the enumeration before porting this engine.
   */
  var STOPWORDS = [
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'from',
    'if', 'in', 'into', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'their',
    'then', 'there', 'these', 'they', 'this', 'to', 'was', 'with'
  ];

  /*
   * Strip characters that carry meaning in Shopify's search grammar so a
   * visitor's punctuation cannot alter the query's structure. Hyphens are
   * deliberately KEPT - product codes depend on them (title:IN-171 resolves
   * to 7 results where the plain code search returns 113).
   */
  function sanitizeTerm(term) {
    return term.replace(/["'()\\:*~^\[\]{}<>]/g, '').trim();
  }

  /*
   * PRODUCT CODES take a different path. Titles carry codes like "IN-171", but
   * the hyphen makes plain search fuzzy: /search?q=IN-171 returns 113 results
   * with LS-171 at rank 1. The general tight tier is no better on rank -
   * (title:IN-171 OR tag:IN-171) returns 7 with FS-171 at rank 1.
   *
   * Quoting the hyphenated value inside the field scope is the whole fix:
   *   title:"IN-171"  ->  1 result, correct product.
   * Measured across 70 codes x 4 typed forms = 280 live queries, 280/280 exact
   * and rank-1 correct. Stable across 5 consecutive runs per shape.
   *
   * The digit normalisation is load-bearing, not cosmetic. The same code exists
   * in three paddings on this store - title "IN-171", handle "in-00171", SKU
   * "IN-00171-PC1001" - and ONLY the min-2-digit title form resolves:
   *   title:"UT-08" -> 1     title:"UT-8" -> 0     title:"UT-008" -> 0
   * Normalising also means a code copied off an invoice ("IN-00171") still works.
   *
   * Why {2,5} digits and not {1,5}: "CA-1"/"US-1"/"SR-1" are highway queries, and
   * title:"CA-01" resolves to a real product (CA-01 Sun Kissed California) - so a
   * one-digit rule would hijack a customer looking for Highway 1. Printed codes
   * are always >=2 digits, so the floor costs nothing.
   *
   * Verified not to misfire: 0/15,000 real product titles match, and 0/48
   * realistic queries match (Coca-Cola, T-shirt, x-ray, B-52, P-51 Mustang,
   * 1940-1950, Highway 101, ISBNs).
   */
  var CODE_RX = /^\s*([A-Za-z]{2})-(\d{2,5})\s*$/;

  /*
   * SH Updated: October 2026 - codes are looked up by SKU, title kept as the
   * fallback.
   *
   * Shopify's storefront search drops a handful of English words even inside a
   * quoted, field-scoped phrase, so title:"IF-53" is read as just 53 and opens
   * every image with that number. Measured live on both stores Oct 28 2026
   * (Cate's report): prefixes AN AS AT BE BY IF IN IS IT OR break; AM CO HI ME
   * MR MY OH OK do not. IN-171 resolved when the August measurement above was
   * taken and no longer does, so the cause sits on Shopify's side and can move
   * again. The SKU carries the code in its five-digit form ("IN-00171-PC1001"),
   * and variants.sku:IN-00171* is not touched by the word list.
   *
   * Coverage, complete bulk export Oct 29 2026: the SKU form reaches the right
   * image, and only that image, for 58,603 of 58,606 coded published retail
   * products and 59,829 of 59,851 wholesale. The rest (unpadded XY/MU SKUs, an
   * "F" prefixed SKU, one product with no SKU) return 0 and take the title
   * form next, which is exactly what shipped before (see hydrate, qc=2).
   */
  function parseCode(raw) {
    var m = CODE_RX.exec(raw || '');
    if (!m) return null;
    return { prefix: m[1].toUpperCase(), num: parseInt(m[2], 10) };
  }

  function pad(num, width) {
    var s = String(num);
    while (s.length < width) s = '0' + s;
    return s;
  }

  function buildCodeQuery(raw) {
    var c = parseCode(raw);
    if (!c) return null;
    return 'variants.sku:' + c.prefix + '-' + pad(c.num, 5) + '*';
  }

  function buildTitleCodeQuery(raw) {
    var c = parseCode(raw);
    if (!c) return null;
    return 'title:"' + c.prefix + '-' + pad(c.num, 2) + '"';
  }

  /*
   * SH Updated: August 2026 - synonym pairs (committed scope add: Greece/Greek).
   * Data-driven: a term matching any member of a group expands its clause to OR
   * across the whole group, so the AND structure is untouched. Verified live:
   * (title:greece OR tag:greece) = 47, greek alone = 173, the expanded 4-way OR
   * clause = 174 with every greece-only page-1 product retained (28/28).
   * Tight tier only - the broaden path keeps the human words untouched, and the
   * product-code path returns before any of this runs.
   */
  var SH_SYNONYMS = [['greece', 'greek']];

  function synonymGroup(term) {
    for (var i = 0; i < SH_SYNONYMS.length; i++) {
      if (SH_SYNONYMS[i].indexOf(term) !== -1) return SH_SYNONYMS[i];
    }
    return null;
  }

  function tokenize(raw) {
    return raw
      .toLowerCase()
      .split(/[\s,;.\/]+/)
      .map(sanitizeTerm)
      .filter(function (t) { return t.length > 0; })
      .filter(function (t) { return STOPWORDS.indexOf(t) === -1; })
      /*
       * SH Updated: August 2026 - de-duplicate repeated terms. "Will Rogers, Will
       * You Be My Valentine" tokenised to will/rogers/will/you/my/valentine and
       * built (title:will OR tag:will) TWICE. Harmless to the result set, since
       * ANDing a clause with itself changes nothing, but it doubles a clause in a
       * URL the customer can see and share. Order is preserved: first occurrence
       * wins. Note the comma is already a token separator and MUST stay one, so
       * "Bloomington, Indiana" keeps ANDing both words. That is the behaviour the
       * relevance work was bought for.
       */
      .filter(function (t, i, all) { return all.indexOf(t) === i; });
  }

  function clauseFor(term) {
    var group = synonymGroup(term) || [term];
    return '(' + group
      .map(function (t) { return 'title:' + t + ' OR tag:' + t; })
      .join(' OR ') + ')';
  }

  /*
   * SH Updated: August 2026 - format words ("bloomington indiana postcard").
   * Formats are VARIANTS on this store, not products - option "Product" with
   * values "Postcard, Pack of 10" / "Magnet, Pack of 6" / "Greeting Card, Pack
   * of 4" / "Art Print, Individual Print" (sampled live from
   * /products/<handle>.js). A format word left in the AND clause zeroes the
   * tier, because no title or tag carries "postcard". So it is lifted out and
   * applied as a variants.title: clause instead. Verified live on the Liquid
   * /search endpoint: (route 66 tight) = 50; + AND variants.title:postcard = 48,
   * dropping exactly the two book products that have no postcard variant; and
   * variants.title:zzznonexist = 0, so the field genuinely filters rather than
   * being ignored.
   * "sticker" is deliberately absent: variants.title:sticker = 0 catalogue-wide
   * (variants.title:postcard = 25,001 by the same query shape, so that zero is
   * real, not a broken query). Lifting a format no variant carries would blank
   * every tile; left in the clause it gets the honest "No exact matches" state
   * plus the broaden path.
   */
  var SH_FORMATS = {
    'postcard': 'postcard', 'postcards': 'postcard',
    'magnet': 'magnet', 'magnets': 'magnet',
    'card': 'greeting card', 'cards': 'greeting card',
    'print': 'art print', 'prints': 'art print'
  };

  // Two-word forms, checked before singles so "greeting card" is one format,
  // not the subject word "greeting" plus a lifted "card".
  var SH_FORMAT_BIGRAMS = {
    'greeting card': 'greeting card', 'greeting cards': 'greeting card',
    'art print': 'art print', 'art prints': 'art print'
  };

  // The single token proven to discriminate on variants.title for each format
  // (multi-word values cannot be quoted here - the sanitizer strips quotes from
  // visitor input, and a bare space would split the clause).
  var SH_FORMAT_FIELD_TOKEN = {
    'postcard': 'postcard',
    'magnet': 'magnet',
    'greeting card': 'greeting',
    'art print': 'print'
  };

  // Splits tokens into subject terms and (at most one) requested format.
  function liftFormat(terms) {
    var subject = [];
    var format = null;
    for (var i = 0; i < terms.length; i++) {
      var bigram = i + 1 < terms.length ? terms[i] + ' ' + terms[i + 1] : null;
      if (bigram && SH_FORMAT_BIGRAMS[bigram]) {
        format = SH_FORMAT_BIGRAMS[bigram];
        i++;
        continue;
      }
      if (SH_FORMATS[terms[i]]) {
        format = SH_FORMATS[terms[i]];
        continue;
      }
      subject.push(terms[i]);
    }
    return { subject: subject, format: format };
  }

  /*
   * The full tight-tier analysis: tokens -> subject clauses (synonym-expanded)
   * AND an optional variants.title: format clause. A subject-less format query
   * ("postcards") still works - variants.title:postcard alone returns the
   * format's whole catalogue. The format also travels as ?qf= so hydration can
   * land each tile on the right variant.
   */
  function analyzeQuery(raw) {
    if (!raw) return null;

    var terms = tokenize(raw);
    if (terms.length === 0) return null;

    var lifted = liftFormat(terms);
    var clauses = lifted.subject.map(clauseFor);
    if (lifted.format) {
      clauses.push('variants.title:' + SH_FORMAT_FIELD_TOKEN[lifted.format]);
    }

    return { machine: clauses.join(' AND '), format: lifted.format };
  }

  function buildTightQuery(raw) {
    var analysis = analyzeQuery(raw);
    return analysis ? analysis.machine : null;
  }

  function buildSearchUrl(root, machine, human, isCode, format) {
    var params = [
      'q=' + encodeURIComponent(machine),
      'qt=' + encodeURIComponent(human),
      'type=product',
      'options%5Bprefix%5D=last'
    ];
    // Marks a code lookup so a misfire can fall back to the raw query (see hydrate).
    if (isCode) params.push('qc=1');
    // The requested format, so hydration can land tiles on the right variant.
    if (format) params.push('qf=' + encodeURIComponent(format));
    return root + '?' + params.join('&');
  }

  function buildPlainUrl(root, human) {
    return root + '?q=' + encodeURIComponent(human) +
      '&type=product&options%5Bprefix%5D=last';
  }

  /*
   * Mirrors blockHandle in assets/theme.js (Part A infinite-scroll de-dup) -
   * keep the two in sync. Identity is the product handle from the block's
   * /products/<handle> href; the href may or may not carry a query string, so
   * only the path segment is read.
   */
  function blockHandle(el) {
    var a = el.querySelector('a.img-link') ||
      el.querySelector('a.product-block-title') ||
      el.querySelector('a[href*="/products/"]');
    var href = a ? a.getAttribute('href') || '' : '';
    var m = href.match(/\/products\/([^\/?#]+)/);
    return m ? m[1].toLowerCase() : null;
  }

  /*
   * SH Updated: August 2026 - variant-level landing for format queries.
   * The template emits one JSON blob ([data-sh-tile-data], outside the masonry
   * grid): handle -> [[variant title, variant id], ...]. When ?qf= names a
   * format, every tile whose product carries a matching variant gets its links
   * rewritten to ?variant=<id>; the rare tile without one (the query-side
   * variants.title clause makes this belt-and-braces) is hidden, using the
   * theme's own row-limit pattern of .hidden + remasonry.
   */
  function readTileMap(rootNode) {
    var el = rootNode.querySelector('[data-sh-tile-data]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  // Variant titles read "Postcard, Pack of 10" - the format is the prefix.
  function variantIdForFormat(variants, format) {
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i][0]).toLowerCase().indexOf(format) === 0) {
        return variants[i][1];
      }
    }
    return null;
  }

  function rewriteTileLinks(block, variantId) {
    Array.prototype.forEach.call(
      block.querySelectorAll('a[href*="/products/"]'),
      function (a) {
        var href = a.getAttribute('href');
        if (!href || /[?&]variant=/.test(href)) return;
        var hashIdx = href.indexOf('#');
        var hash = hashIdx === -1 ? '' : href.slice(hashIdx);
        var path = hashIdx === -1 ? href : href.slice(0, hashIdx);
        a.setAttribute(
          'href',
          path + (path.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variantId + hash
        );
      }
    );
  }

  /*
   * Returns the blocks that carry the requested format (links rewritten).
   * In-document blocks lacking it are hidden in place; detached blocks (the
   * broad-tier fetch) are simply not returned, so they are never appended.
   * A block whose product is not in the map is left untouched and kept -
   * never hidden on a guess.
   */
  function applyFormat(blocks, map, format) {
    var kept = [];
    var hidInPlace = false;
    Array.prototype.forEach.call(blocks, function (block) {
      var handle = blockHandle(block);
      if (!handle || !map[handle]) {
        kept.push(block);
        return;
      }
      var id = variantIdForFormat(map[handle], format);
      if (id === null) {
        if (block.parentNode) {
          block.classList.add('hidden');
          hidInPlace = true;
        }
        return;
      }
      rewriteTileLinks(block, id);
      kept.push(block);
    });
    if (hidInPlace && window.theme && window.theme.MasonryManager) {
      window.theme.MasonryManager.remasonry();
    }
    return kept;
  }

  /*
   * SH Updated: September 2026 - one place to retire the count surfaces.
   *
   * Two different things can make the server's total stop describing the
   * screen: the phrase tier splitting the page, and a format query hiding the
   * tiles whose variant does not really match. Cate reads and quotes these
   * lines verbatim ("1-100 of 1000", "13 images"), so a number that disagrees
   * with what is on screen is worse than no number at all. The page LINKS are
   * never touched - only the two places that state a quantity.
   */
  function hideCountSurfaces() {
    var count = document.querySelector('.sh-search-count');
    if (count) count.setAttribute('hidden', '');
    Array.prototype.forEach.call(
      document.querySelectorAll('.pagination__summary'),
      function (el) { el.setAttribute('hidden', ''); }
    );
  }

  /*
   * SH Updated: August 2026 - in-page broaden ("Show more related products").
   * Fetches the PLAIN query through the Section Rendering API
   * (&section_id=search-template - static sections use the filename as ID;
   * verified live on this store: returns the rendered section with product
   * blocks and its own pagination, and honours the theme-preview cookie).
   * Survivors are appended into a second grid under "Other related products";
   * a tile whose handle is already anywhere on the page is dropped.
   * End-of-results reads the fetched fragment's own a.next, never how many
   * tiles survived de-duplication, so the filter cannot cause a premature end.
   * NO count is ever rendered for this tier - the plain query's totals drift
   * between identical requests.
   */
  function setupBroaden(human, format) {
    var box = document.querySelector('[data-sh-broaden]');
    var link = document.querySelector('[data-sh-broaden-link]');
    if (!box || !link) return;

    var root = link.getAttribute('href') || '/search';
    var plainUrl = buildPlainUrl(root, human);
    // The href stays a real plain-search URL: if jQuery or the masonry manager
    // is unavailable the click falls through to the old navigate-away broaden.
    link.setAttribute('href', plainUrl);
    box.removeAttribute('hidden');

    var tier = document.querySelector('[data-sh-broad-tier]');
    var grid = tier ? tier.querySelector('[data-sh-broad-grid]') : null;
    var $ = window.jQuery;
    if (!tier || !grid || !$ || !window.theme || !window.theme.MasonryManager) return;

    var seenHandles = {};
    var fetchedUrls = {};
    var emptyBatchStreak = 0;
    var EMPTY_BATCH_LIMIT = 5;
    var nextUrl = plainUrl;
    var busy = false;
    var idleText = link.textContent;

    /*
     * Seed from every tile already on the page, all tiers.
     * SH Updated: August 2026 - re-read per batch rather than once at setup.
     * The phrase tier appends its tiles asynchronously after this function
     * runs, so a seed taken here would miss any phrase match that is not also
     * in the tight tier, and broaden would render it a second time.
     */
    function reseed() {
      Array.prototype.forEach.call(
        document.querySelectorAll('.blocklayout .block'),
        function (el) {
          var h = blockHandle(el);
          if (h) seenHandles[h] = true;
        }
      );
    }
    reseed();

    function finish() {
      nextUrl = null;
      box.setAttribute('hidden', '');
    }

    function fetchBatch() {
      if (!nextUrl || fetchedUrls[nextUrl]) {
        // Same page twice - hard stop. Deliberately no "no more products"
        // claim: that is not a claim this code can support.
        finish();
        return;
      }
      reseed();
      var url = nextUrl;
      fetchedUrls[url] = true;
      busy = true;
      link.textContent = (window.theme.strings &&
        window.theme.strings.products_listing_loading) || 'Loading…';

      var sep = url.indexOf('?') === -1 ? '?' : '&';
      $.get(url + sep + 'section_id=search-template', function (data) {
        // DOMParser keeps the fragment inert - nothing in it can execute.
        var doc = new DOMParser().parseFromString(data, 'text/html');

        var survivors = [];
        Array.prototype.forEach.call(
          doc.querySelectorAll('.blocklayout .block'),
          function (el) {
            var h = blockHandle(el);
            if (h && seenHandles[h]) return; // already on the page
            if (h) seenHandles[h] = true;
            // Unknown identity is kept, never dropped silently.
            survivors.push(document.importNode(el, true));
          }
        );

        if (format) {
          // The fetched fragment carries its own tile map, so the broad tier
          // lands on the requested variant with no second fetch.
          survivors = applyFormat(survivors, readTileMap(doc), format);
        }

        if (survivors.length) {
          emptyBatchStreak = 0;
          tier.removeAttribute('hidden');
          window.theme.MasonryManager.appendItems($(survivors), $(grid));
          // The grid was initialised while hidden, so its widths were computed
          // against a zero-width container - recompute now it is visible.
          window.theme.MasonryManager.remasonry();
        } else {
          emptyBatchStreak++;
        }

        busy = false;
        link.textContent = idleText;

        var next = doc.querySelector('.pagination a.next');
        if (!next || !next.getAttribute('href')) {
          finish();
          return;
        }
        nextUrl = next.getAttribute('href');

        if (!survivors.length && emptyBatchStreak < EMPTY_BATCH_LIMIT && !fetchedUrls[nextUrl]) {
          // Whole batch was duplicates, so the page did not grow - advance once
          // more ourselves. Bounded by the streak limit and fetchedUrls, and it
          // runs inside the AJAX callback, so there is no recursion.
          fetchBatch();
        }
      }).fail(function () {
        delete fetchedUrls[url]; // failed request stays retryable
        busy = false;
        link.textContent = idleText;
      });
    }

    link.addEventListener('click', function (ev) {
      ev.preventDefault();
      if (busy) return;
      fetchBatch();
    }, false);
  }

  /*
   * SH Updated: August 2026 - phrase-first tier ("Best matches").
   *
   * WHY: a fully field-scoped query is a correct FILTER but not a RANKING.
   * Shopify returns one in a fixed order (alphabetical by handle), so the
   * tight tier for "black cat" led with AM-353 Wizard Conjuring Tobacco
   * Leaves, AM-939 Join the Tanks and AN-46 Circus Lion, and put KC-238
   * "Black Cat" at 109 of 130. Cate reported exactly that on Aug 28 2026.
   * Booked scope line 1 is "exact/adjacent-phrase matches on top", so this
   * is the missing half of the tier, not a new feature.
   *
   * WHY IT IS NOT FIXED IN THE QUERY. A bare quoted phrase DOES rank -
   * "black cat" alone returns KC-238 first, deterministically (63/63/63).
   * But the moment a bare phrase is combined with field-scoped clauses,
   * Shopify stops honouring them. Measured on this dev theme:
   *   '"black cat" OR ((title:black OR tag:black) AND (title:cat OR tag:cat))'
   *   returns 101 where the field-scoped AND alone returns 130, and the 29 it
   *   drops are REAL matches lost to the singular/plural gap - HW-80 "Black
   *   Cats on Fence", HW-134 "Flying Black Cats", KC-88 "French Black Cats in
   *   Snow", KC-90/91/92.
   *   '"black cat" AND variants.title:postcard' and the magnet equivalent both
   *   return the same 63, so the format clause is ignored too.
   * The tight query therefore stays exactly as it is, and the phrase tier is
   * fetched SEPARATELY and rendered above it. Nothing is removed from the
   * result set - the phrase matches are lifted out of it and shown first.
   *
   * Same Section Rendering fetch the broaden tier uses, so on wholesale the
   * fragment is rendered through the EasyLockdown filter server-side and a
   * gated product can never reach the page.
   */
  function setupBestTier(human, format) {
    var tier = document.querySelector('[data-sh-best-tier]');
    var grid = tier ? tier.querySelector('[data-sh-best-grid]') : null;
    var $ = window.jQuery;
    if (!tier || !grid || !$ || !window.theme || !window.theme.MasonryManager) return;

    /*
     * PAGE 1 ONLY. The phrase tier is a re-ranking laid over a PAGINATED list,
     * and the two fight: rendered on page 2 it repeated all of its tiles above
     * the tail, so a visitor clicking through saw the same images twice.
     * Caught on the preview Aug 31 before the client did. Page 2 and beyond are
     * therefore left exactly as they were before this tier existed.
     */
    var page = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    if (page > 1) return;

    // A single word IS its own phrase - the tight tier already is that query.
    var lifted = liftFormat(tokenize(human));
    if (lifted.subject.length < 2) return;

    // Format words are lifted OUT of the phrase and applied to the fetched
    // tiles client-side, exactly as the broad tier does, because the query
    // side ignores a format clause next to a bare phrase (see above).
    var url = '/search?q=' +
      encodeURIComponent('"' + lifted.subject.join(' ') + '"') + '&type=product';

    $.get(url + '&section_id=search-template', function (data) {
      // DOMParser keeps the fragment inert - nothing in it can execute.
      var doc = new DOMParser().parseFromString(data, 'text/html');

      var best = [];
      Array.prototype.forEach.call(
        doc.querySelectorAll('.blocklayout .block'),
        function (el) { best.push(document.importNode(el, true)); }
      );
      if (!best.length) return;

      if (format) {
        best = applyFormat(best, readTileMap(doc), format);
        if (!best.length) return;
      }

      // Index what the main grid is already showing, so nothing renders twice.
      var onPage = {};
      Array.prototype.forEach.call(
        document.querySelectorAll('.blocklayout .block'),
        function (el) {
          var h = blockHandle(el);
          if (h && !onPage[h]) onPage[h] = el;
        }
      );

      best.forEach(function (el) {
        var h = blockHandle(el);
        // Hide, never remove: the theme's own row-limit pattern, and it keeps
        // the tile-data blob and the broaden de-dup seed intact.
        if (h && onPage[h]) onPage[h].classList.add('hidden');
      });

      tier.removeAttribute('hidden');
      window.theme.MasonryManager.appendItems($(best), $(grid));
      // The grid was initialised while hidden, so its widths were computed
      // against a zero-width container - recompute now it is visible.
      window.theme.MasonryManager.remasonry();

      /*
       * The count counts BOTH tiers, so once the page is split it describes
       * neither half. See hideCountSurfaces.
       */
      hideCountSurfaces();

      // Only label the lower tier if something visible is actually left in it.
      var remaining = 0;
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-sh-main-grid] .block'),
        function (el) { if (!el.classList.contains('hidden')) remaining++; }
      );
      var other = document.querySelector('[data-sh-other-heading]');
      if (other && remaining) other.removeAttribute('hidden');
    });
  }

  /*
   * The engine URL for a typed phrase, or null when there is nothing usable
   * (empty, or all stopwords) and the caller should let the stock behaviour
   * through. Shared by the form submit and by the type-ahead "see all" link,
   * so the two can never drift apart again.
   */
  function engineUrlFor(root, raw) {
    var human = (raw || '').trim();

    // A product code takes the exact-match path - it bypasses synonyms and
    // format lifting entirely; everything else gets the tight-tier analysis.
    var code = buildCodeQuery(human);
    var analysis = code ? null : analyzeQuery(human);
    var machine = code || (analysis && analysis.machine);
    if (!machine) return null;

    return buildSearchUrl(
      root, machine, human, !!code, analysis && analysis.format
    );
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var url = engineUrlFor(form.getAttribute('action') || '/search', input.value);
    if (!url) return;

    event.preventDefault();
    window.location.href = url;
  }

  document.addEventListener('submit', onSubmit, false);

  /*
   * SH Updated: August 2026 - the type-ahead panel's "see all" link.
   *
   * theme.js builds that link as form.action + '?' + form.serialize(), so it
   * carried the visitor's raw words straight to the PLAIN search - the exact
   * behaviour this engine exists to replace. It bypassed the engine because
   * it is an <a href> click and the engine hooks form submit.
   *
   * Cate reported the symptom on Aug 28 2026: "red shoes" through the retail
   * panel gave "1-100 of 1000", the same words typed on wholesale (no panel)
   * gave 13 sensible results. Same engine on both stores; only the exit link
   * differed. Verified at the link itself - its q carried no title: clause.
   *
   * Delegated, because theme.js re-creates the link on every keystroke.
   */
  function closestShowAll(node) {
    while (node && node !== document) {
      if (node.classList && node.classList.contains('quick-search__show-all')) return node;
      node = node.parentNode;
    }
    return null;
  }

  function onShowAllClick(event) {
    if (!closestShowAll(event.target)) return;
    var form = document.querySelector('.quick-search__form');
    var input = form ? form.querySelector('input[name="q"]') : null;
    if (!input) return;
    var url = engineUrlFor(form.getAttribute('action') || '/search', input.value);
    if (!url) return; // empty or all stopwords - let the stock link through
    event.preventDefault();
    window.location.href = url;
  }

  document.addEventListener('click', onShowAllClick, false);


  /*
   * Display half. Liquid cannot read ?qt=, so the template renders the raw
   * field-scoped query as a placeholder and we swap in the human terms here.
   * Also points the broaden link at the plain query and reveals it.
   *
   * Safe by construction: a field-scoped q only ever exists because this
   * script built it, so it is always present to clean up after itself.
   */
  /*
   * SH Updated: September 2026 - keep the visitor's own words alive across
   * pagination.
   *
   * Shopify builds paginate.parts URLs from the search params it RECOGNISES
   * (q, type, options[prefix], page) and silently drops every custom one, so
   * page 2 arrived with no ?qt= at all. hydrate() then returned at its first
   * line and the entire display half never ran: the raw machine query showed
   * in the search box, the phrase tier never rendered, the broaden control
   * stayed hidden and the tab title lost the terms. Clicking back to page 1
   * through a page link landed on that same qt-less URL, which is why the
   * results returned in a visibly different order - the Best matches tier was
   * simply absent, leaving the unranked field-scoped order underneath.
   *
   * Measured on the retail dev theme Sep 1 2026: the page-2 href carried
   * exactly options[prefix], page, q, type. Cate reported all four symptoms
   * from this one cause on Aug 31.
   */
  function carryTermsThroughPagination(params, human) {
    var carry = { qt: human };
    ['qf', 'qc'].forEach(function (k) {
      if (params.get(k) !== null) carry[k] = params.get(k);
    });

    Array.prototype.forEach.call(
      document.querySelectorAll('.pagination a[href]'),
      function (a) {
        var href = a.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;
        var url;
        try {
          url = new URL(href, window.location.origin);
        } catch (e) {
          return; // leave anything unparseable exactly as the theme built it
        }
        Object.keys(carry).forEach(function (k) {
          url.searchParams.set(k, carry[k]);
        });
        a.setAttribute('href', url.pathname + url.search);
      }
    );
  }

  /*
   * SH Updated: September 2026 - recover the human terms FROM the machine
   * query when ?qt= is missing altogether.
   *
   * The belt to the pagination braces above. A bookmarked, shared or
   * hand-edited results URL carries no qt either, and without this the search
   * box would still show a visitor "(title:black OR tag:black) AND (title:cat
   * OR tag:cat)". The machine query is built by this script to a known
   * grammar, so it can be read back: the FIRST title: token in each subject
   * clause is the word that was actually typed (synonym expansion only ever
   * appends further OR'd pairs after it), and a variants.title: clause maps
   * back to its format word.
   *
   * Round-tripped against buildTightQuery across the live format vocabulary
   * and multi-word, quoted and synonym-expanded queries before being trusted
   * for anything beyond display.
   */
  function humanFromMachine(machine) {
    // SH Updated: October 2026 - a SKU code lookup reads back as the code.
    var sku = /^variants\.sku:([A-Za-z]{2})-(\d{5})\*$/.exec(machine || '');
    if (sku) return sku[1].toUpperCase() + '-' + pad(parseInt(sku[2], 10), 2);

    if (!machine || machine.indexOf('title:') === -1) return null;

    var words = [];
    var format = null;

    machine.split(' AND ').forEach(function (clause) {
      var body = clause.replace(/^\(+/, '').replace(/\)+$/, '');

      if (body.indexOf('variants.title:') === 0) {
        var token = body.slice('variants.title:'.length);
        Object.keys(SH_FORMAT_FIELD_TOKEN).forEach(function (f) {
          if (SH_FORMAT_FIELD_TOKEN[f] === token) format = f;
        });
        return;
      }

      var m = body.match(/title:(?:"([^"]+)"|([^\s)]+))/);
      if (m) words.push(m[1] || m[2]);
    });

    if (format) words.push(format);
    return words.length ? words.join(' ') : null;
  }

  function hydrate() {
    var params = new URLSearchParams(window.location.search);
    var human = params.get('qt') || humanFromMachine(params.get('q'));
    if (!human) return;

    /*
     * Code-lookup fallback. The code pattern is anchored and tested clean against
     * 15,000 real titles and 48 realistic queries, but one residual misfire is
     * known and measured: "US-101" (a highway) matches the shape, and
     * title:"US-101" returns 0. Rather than dead-end the visitor on an empty
     * page, re-run their raw words. Zero results on a code lookup always means
     * typo / discontinued / misfire - never a meaningful "no matches".
     *
     * Deliberately scoped to code lookups only. For ordinary searches an empty
     * tight tier IS meaningful, and gets the "No exact matches" state plus the
     * broaden control instead of a silent redirect.
     */
    if (params.get('qc')) {
      var totalEl = document.querySelector('[data-results-total]');
      var total = totalEl ? parseInt(totalEl.getAttribute('data-results-total'), 10) : null;
      if (total === 0) {
        // SH Updated: October 2026 - a SKU lookup (qc=1) that finds nothing
        // tries the title form first (qc=2), then the raw words.
        var titleCode = params.get('qc') === '1' ? buildTitleCodeQuery(human) : null;
        window.location.replace(titleCode
          ? buildSearchUrl(window.location.pathname, titleCode, human, false, null) + '&qc=2'
          : buildPlainUrl(window.location.pathname, human));
        return;
      }
    }

    carryTermsThroughPagination(params, human);

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-sh-search-terms]'),
      function (el) { el.textContent = human; }
    );

    // Reveal the " for “terms”" fragment only once it has real terms in it.
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-sh-terms-wrap]'),
      function (el) { el.removeAttribute('hidden'); }
    );

    var format = params.get('qf');
    /*
     * ?qf= is dropped by the same pagination rebuild that drops ?qt=, and a
     * bookmarked URL never had it. When the terms were recovered rather than
     * read, re-lift the format from them with the same tested code path that
     * put it there.
     */
    if (!format && !params.get('qt')) {
      var relifted = liftFormat(tokenize(human));
      if (relifted.format) format = relifted.format;
    }
    if (format) {
      applyFormat(
        document.querySelectorAll('.blocklayout .block'),
        readTileMap(document),
        format
      );

      /*
       * SH Updated: September 2026 - a format query hides the tiles whose
       * variant does not actually match, because variants.title: matches
       * loosely server-side. Page 1 usually loses nothing, so this stayed
       * invisible; page 2 of "postcards" lost 43 of 100 and the line above it
       * still read "Showing 101 - 200 of 242 results" over 57 tiles. Retire
       * the numbers whenever anything was actually hidden, and only then.
       *
       * This became reachable on page 2 only when ?qt= started surviving
       * pagination, so it ships with that change rather than after it.
       */
      if (document.querySelector('[data-sh-main-grid] .block.hidden')) {
        hideCountSurfaces();
      }
    }

    setupBestTier(human, format);
    setupBroaden(human, format);

    // Put the visitor's own words back in the box, not the machine query.
    Array.prototype.forEach.call(
      document.querySelectorAll('form input[name="q"]'),
      function (input) { input.value = human; }
    );

    /*
     * The tab title, bookmarks and browser history would otherwise read
     * 'Search: 50 results found for "(title:route OR tag:route) AND ..."'.
     * The layout renders a neutral placeholder server-side; put the visitor's
     * own words back here.
     */
    document.title = document.title.replace(
      /^Search results/,
      'Search results for “' + human + '”'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate, false);
  } else {
    hydrate();
  }

  // Exposed for the template's "broaden" control and for verification.
  window.shSearchRelevance = {
    buildTightQuery: buildTightQuery,
    buildSearchUrl: buildSearchUrl,
    analyzeQuery: analyzeQuery,
    engineUrlFor: engineUrlFor,
    humanFromMachine: humanFromMachine,
    buildCodeQuery: buildCodeQuery,
    buildTitleCodeQuery: buildTitleCodeQuery
  };
})();
